import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiClientError } from '@/services/apiClient';
import { FEATURE_CATALOG, recruiterApi, type Feature, type FeatureInfo, type RecruiterTrial, type TrialInput } from '@/services/recruiterApi';
import { FeatureToggles } from './FeatureToggles';

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error' | 'disabled';
interface FormState { id: string | null; name: string; days: string; features: Feature[]; adsPerStream: string; freeStreamsPerDay: string; }
const EMPTY: FormState = { id: null, name: '', days: '7', features: [], adsPerStream: '', freeStreamsPerDay: '' };

export function Trials(): JSX.Element {
  const { t } = useTranslation();
  const [trials, setTrials] = useState<RecruiterTrial[]>([]);
  // Seed from the static catalog so the feature selector always renders.
  const [features, setFeatures] = useState<FeatureInfo[]>([...FEATURE_CATALOG]);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setStatus('loading');
    try {
      const { trials } = await recruiterApi.listTrials(false);
      setTrials(trials); setStatus('ready');
    } catch (err) {
      setStatus(err instanceof ApiClientError && err.status === 404 ? 'disabled' : 'error');
      return;
    }
    try {
      const { features } = await recruiterApi.listFeatures();
      if (features?.length) setFeatures(features);
    } catch {
      /* keep the static FEATURE_CATALOG */
    }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  const toForm = (tr: RecruiterTrial): FormState => ({
    id: tr.id, name: tr.name, days: String(tr.days), features: [...tr.features],
    adsPerStream: tr.adConfig?.adsPerStream != null ? String(tr.adConfig.adsPerStream) : '',
    freeStreamsPerDay: tr.adConfig?.freeStreamsPerDay != null ? String(tr.adConfig.freeStreamsPerDay) : '',
  });

  function buildInput(f: FormState): TrialInput {
    const adConfig: { adsPerStream?: number; freeStreamsPerDay?: number } = {};
    if (f.adsPerStream.trim()) adConfig.adsPerStream = Number(f.adsPerStream);
    if (f.freeStreamsPerDay.trim()) adConfig.freeStreamsPerDay = Number(f.freeStreamsPerDay);
    return { name: f.name.trim(), days: Number(f.days || '1'), features: f.features, adConfig: Object.keys(adConfig).length ? adConfig : null };
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!form) return;
    setBusy(true); setError(null);
    try {
      const input = buildInput(form);
      if (form.id) await recruiterApi.updateTrial(form.id, input); else await recruiterApi.createTrial(input);
      setForm(null); await refresh();
    } catch (err) { setError(err instanceof Error ? err.message : t('recruitment.common.actionFailed')); }
    finally { setBusy(false); }
  }

  async function handleArchive(id: string): Promise<void> {
    if (!window.confirm(t('recruitment.trials.confirmArchive'))) return;
    try { await recruiterApi.archiveTrial(id); await refresh(); }
    catch (err) { setError(err instanceof Error ? err.message : t('recruitment.common.actionFailed')); }
  }

  function toggleFeature(key: Feature): void {
    setForm((f) => (f ? { ...f, features: f.features.includes(key) ? f.features.filter((x) => x !== key) : [...f.features, key] } : f));
  }

  return (
    <main className="admin-page">
      <header className="admin-page__header">
        <h1>{t('recruitment.trials.title')}</h1>
        <p className="muted">{t('recruitment.trials.intro')}</p>
      </header>
      <section className="admin-card">
        {status === 'loading' && <p className="muted" role="status">{t('recruitment.common.loading')}</p>}
        {status === 'error' && <p className="error" role="alert">{t('recruitment.common.actionFailed')}</p>}
        {status === 'disabled' && <p className="muted" role="status">{t('recruitment.common.disabled')}</p>}
        {status === 'ready' && trials.length === 0 && <p className="muted">{t('recruitment.trials.empty')}</p>}
        {status === 'ready' && trials.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">{t('recruitment.trials.table.name')}</th>
                  <th scope="col" className="num">{t('recruitment.trials.table.days')}</th>
                  <th scope="col" aria-label={t('recruitment.trials.table.actions')} />
                </tr>
              </thead>
              <tbody>
                {trials.map((tr) => (
                  <tr key={tr.id}>
                    <td>{tr.name}</td>
                    <td className="num">{tr.days}</td>
                    <td className="actions">
                      <button type="button" className="btn-ghost" onClick={() => setForm(toForm(tr))}>{t('recruitment.common.edit')}</button>
                      <button type="button" className="btn btn-outline" onClick={() => void handleArchive(tr.id)}>{t('recruitment.common.archive')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!form && status !== 'disabled' && <button type="button" className="btn" style={{ marginTop: 12 }} onClick={() => setForm({ ...EMPTY })}>{t('recruitment.trials.newTrial')}</button>}
      </section>

      {form && (
        <section className="admin-card">
          <h2 className="admin-card__title">{form.id ? t('recruitment.trials.editTrial') : t('recruitment.trials.newTrial')}</h2>
          <form className="recruiter-form" onSubmit={handleSubmit} noValidate>
            <div className="recruiter-form__grid">
              <div className="field">
                <label htmlFor="tr-name">{t('recruitment.trials.form.name')}</label>
                <input id="tr-name" value={form.name} maxLength={80} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="tr-days">{t('recruitment.trials.form.days')}</label>
                <input id="tr-days" type="number" min="1" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} />
              </div>
            </div>

            <div className="field">
              <span className="field__label" id="tr-features-label">{t('recruitment.trials.form.features')}</span>
              <FeatureToggles features={features} selected={form.features} onToggle={toggleFeature} />
            </div>

            <fieldset className="recruiter-form__group">
              <legend className="field__label">{t('recruitment.plans.form.ads')}</legend>
              <div className="recruiter-form__grid">
                <div className="field">
                  <label htmlFor="tr-ads">{t('recruitment.plans.form.adsPerStream')}</label>
                  <input id="tr-ads" type="number" min="0" value={form.adsPerStream} onChange={(e) => setForm({ ...form, adsPerStream: e.target.value })} />
                </div>
                <div className="field">
                  <label htmlFor="tr-free">{t('recruitment.plans.form.freeStreamsPerDay')}</label>
                  <input id="tr-free" type="number" min="0" value={form.freeStreamsPerDay} onChange={(e) => setForm({ ...form, freeStreamsPerDay: e.target.value })} />
                </div>
              </div>
              <small className="muted">{t('recruitment.plans.form.adsHint')}</small>
            </fieldset>

            <div className="form-actions">
              <button type="submit" className="btn" disabled={busy}>{form.id ? t('recruitment.common.save') : t('recruitment.common.create')}</button>
              <button type="button" className="btn-ghost" onClick={() => setForm(null)}>{t('recruitment.common.cancel')}</button>
            </div>
            {error && <p className="error" role="alert">{error}</p>}
          </form>
        </section>
      )}
    </main>
  );
}
